<template>
    <LoadingCard v-if="loading" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>{{ logId ? 'Update' : 'Add a'}} base fertiliser diary entry
                <small class="pull-right" v-if="singleBlock">
                    <strong>Block:</strong> &ldquo;{{ singleBlock.name }}&rdquo; ({{ singleBlock.variety.name }})
                </small>
            </h1>

        </header>
        <form @submit.prevent="attemptCreate" ref="createForm" novalidate>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Required Details
                </div>

                <div class="card-body pt-3">

                    <!-- Blocks -->
                    <div class="form-group row" v-if="!singleBlock">
                        <label class="col-sm-2 col-form-label">Sprayed Blocks</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="selectedBlocks"
                                        :options="blocks"
                                        :hide-selected="true"
                                        placeholder="Blocks..."
                                        label="name"
                                        track-by="id"
                                        :multiple="true"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Spray Date -->
                    <div class="form-group row">
                        <label for="date" class="col-sm-2 col-form-label">Application Date</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="date"
                                    v-model="sprayed_at"
                                    :config="datePicker"
                                    class="form-control"
                                    :class="{'is-invalid': errors.has('sprayed_at')}"
                                    placeholder="Select date"
                                    v-validate="validation.date"
                                    name="sprayed_at" />
                            <i class="form-group__bar"></i>
                            <div class="invalid-feedback" v-if="errors.has('sprayed_at')">{{ errors.first('sprayed_at') }}</div>
                        </div>
                    </div>
                </div>

                <div class="bg-light">
                    <h6 class="card-header text-secondary">Fertilisers Used</h6>
                </div>
                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="entryRows" :fields="entriesTable.fields" :show-empty="true">
                        <template slot="fertiliser" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`fertiliser_${row.index}`) || ''"
                                        @input="setRow('fertiliser', row.index, $event)"
                                        :value="row.item.fertiliser"
                                        v-validate="validation.entry_fertiliser"
                                        :data-vv-name="`fertiliser_${row.index}`"
                                        data-vv-as="fertiliser"
                                        :options="sortedFertilisers"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Fertiliser"
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.rate" class="form-control"
                                         v-b-tooltip="errors.first(`rate_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`rate_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_rate"
                                         :data-vv-name="`rate_${row.index}`"
                                         data-vv-as="rate"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="weight" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.weight" class="form-control"
                                         v-b-tooltip="errors.first(`weight_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`weight_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_weight"
                                         :data-vv-name="`weight_${row.index}`"
                                         data-vv-as="weight"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('weight', row.index, $event)" />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addRow">Add Another Fertiliser</button>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Optional Details
                </div>

                <div class="card-body pt-3">

                    <!-- Comments -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Comments</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <textarea class="form-control" v-model="comments" placeholder="Comments..." v-autosize></textarea>
                                <div class="invalid-feedback" v-if="errors.has('comments')">{{ errors.first('comments') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-6">
                            <button type="button" @click="attemptDelete" v-if="logId" class="btn btn-danger">Delete</button>
                        </div>
                        <div class="col-sm-6 text-right">
                            <button type="submit" class="btn btn-success">{{ logId ? 'Update' : 'Create' }}</button>
                        </div>
                    </div>

                    <div class="text-right">
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import createBaseFertiliserApplicationMutation from '@views/BaseFertiliserDiary/Create/mutationCreateBaseFertiliserApplication.graphql';
import deleteBaseFertiliserApplicationMutation from '@views/BaseFertiliserDiary/Create/mutationDeleteBaseFertiliserApplication.graphql';
import updateBaseFertiliserApplicationMutation from '@views/BaseFertiliserDiary/Create/mutationUpdateBaseFertiliserApplication.graphql';
import currentUserQuery from '@views/BaseFertiliserDiary/Create/queryCurrentUser.graphql';
import findBaseFertiliserApplicationQuery from '@views/BaseFertiliserDiary/Create/queryFindBaseFertiliserApplication.graphql';
import listLinkedFertilisersQuery from '@views/BaseFertiliserDiary/Create/queryListGrowerLinkedFertilisers.graphql';

import ConfirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';

const datePickerConfig = (config = {}) => {
  return {
    plugins: [new ConfirmDatePlugin({ confirmIcon: '' })],
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: false,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

export default {
  name: 'CreateBaseFertiliserDiary',
  props: ['blocks', 'userId', 'logId', 'cropTypeId'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    currentLog: {
      query: findBaseFertiliserApplicationQuery,
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          this.selectedBlocks = [data.findBaseFertiliserApplication.block];
          this.comments = data.findBaseFertiliserApplication.comments;
          this.entryRows = data.findBaseFertiliserApplication.entries.map(item => ({ ...item }));
          this.sprayed_at = data.findBaseFertiliserApplication.sprayed_at;
        }
      },
      variables() {
        return {
          input: {
            id: this.logId,
          },
        };
      },
      skip() {
        return !this.logId;
      },
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              type: 'base',
              user_id: this.userId,
            },
          };
        } else {
          return {
            input: {
              type: 'base',
            },
          };
        }
      },
    },
  },
  data() {
    return {
      loading: 0,
      currentUser: null,
      selectedBlocks: this.blocks.length === 1 ? this.blocks : [],
      sprayed_at: null,
      comments: null,
      entryRows: Array.from({ length: 3 }, () => ({
        fertiliser: null,
        rate: null,
        weight: null,
      })),
      linkedFertilisers: [],
      entriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(kgs-lts /hec)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      datePicker: datePickerConfig({
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      }),
      validation: {
        date: { required: true },
        entry_fertiliser: { required: true },
        entry_rate: { required: true, min_value: 0 },
        entry_weight: { required: true, min_value: 0 },
      },
    };
  },
  computed: {
    singleBlock() {
      if (this.logId) {
        return this.selectedBlocks[0];
      }
      return this.blocks.length === 1 ? this.blocks[0] : null;
    },
    sortedFertilisers() {
      return [...this.linkedFertilisers].sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    addRow() {
      this.entryRows.push({
        fertiliser: null,
        rate: null,
        weight: null,
      });
    },
    removeRow(index) {
      this.entryRows.splice(index, 1);
    },
    setRow(field, index, value) {
      this.entryRows[index][field] = value;
    },
    attemptDelete() {
      const notification = this.$notifyConfirm('Deleting a base fertiliser application entry cannot be undone. Are you sure you wish to delete this?', {
        buttons: [
          this.$notification.button('Yes', 'btn btn-primary', () => {
            notification.close();
            this.doDelete();
          }),
          this.$notification.button('Cancel', 'btn btn-light', () => notification.close()),
        ],
      });
    },
    doDelete() {
      this.$apollo
        .mutate({
          mutation: deleteBaseFertiliserApplicationMutation,
          variables: { input: { id: this.logId } },
        })
        .then(() => {
          this.$notifySuccess(`Application deleted!`);
          this.$router.back();
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    attemptCreate() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const mutationInput = {
          sprayed_at: this.sprayed_at,
          comments: this.comments,
          entries: this.entryRows.map(row => ({
            fertiliser_id: row.fertiliser.id,
            rate: row.rate,
            weight: row.weight,
          })),
        };

        if (this.logId) {
          mutationInput.id = this.logId;
        } else {
          mutationInput.block_id = this.selectedBlocks.map(block => block.id);
        }

        this.$apollo
          .mutate({
            mutation: this.logId ? updateBaseFertiliserApplicationMutation : createBaseFertiliserApplicationMutation,
            variables: {
              input: mutationInput,
            },
          })
          .then(() => {
            this.$notifySuccess(`Base fertiliser application ${this.logId ? 'updated' : 'created'}!`);
            if(this.singleBlock) {
              setTimeout(() => this.$router.push({name: 'base-fertiliser.view', params: {blockId: this.singleBlock.id}}), this.$redirectTimeout);
            } else {
              setTimeout(() => this.$router.push({name: 'base-fertiliser.view'}), this.$redirectTimeout);
            }

            this.$validator.reset();

          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
  },
};
</script>
